import { styled } from '@mui/material'
import { colorPalette } from '../../../style/colorPalette'

const STATUS_CHIP_SHADOW_ALPHA_HEX = '4D'

export type StatusChipProps = {
  /** チップに表示するラベル文字列 */
  label: string
  backgroundColor: string
  textColor: string
  /** CSS の border 値（例: '1px solid #D8DDE5'） */
  borderColor: string
  /**
   * true の場合は文字数に応じて幅を最適化する（最小幅を付けない）。
   * 既定（false）は最小幅 77px を確保し、チップ幅を揃える。
   */
  fitContent?: boolean
}

/**
 * ステータス表示用の共通チップ（見た目のみ）。
 * ステータス体系（WbsItem / プロジェクト / プログラム）を知らない presentational な部品で、
 * 色・ラベルは呼び出し側（features/ 配下のドメイン別ラッパー）が解決して渡す。
 */
export const StatusChip = ({
  label,
  backgroundColor,
  textColor,
  borderColor,
  fitContent = false,
}: StatusChipProps) => (
  <Chip bg={backgroundColor} fg={textColor} bd={borderColor} fit={fitContent}>
    <Label>{label}</Label>
  </Chip>
)

const Chip = styled('span', {
  shouldForwardProp: prop =>
    prop !== 'bg' && prop !== 'fg' && prop !== 'bd' && prop !== 'fit',
})<{ bg: string; fg: string; bd: string; fit: boolean }>(
  ({ bg, fg, bd, fit }) => ({
    display: 'inline-flex',
    // 既定は 77px の固定幅（列を縮めた際はセル幅まで縮む）。fit 指定時は文字数に最適化。
    minWidth: fit ? 'auto' : 'min(77px, 100%)',
    maxWidth: '100%',
    height: 22,
    padding: '4px 8px',
    alignItems: 'center',
    // 固定幅（既定）はラベル左寄せ。fit 指定時は内容幅のため中央寄せでよい。
    justifyContent: fit ? 'center' : 'flex-start',
    boxSizing: 'border-box',
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 500,
    // line-height を詰めすぎると Label の overflow:hidden で descender（g/p など）が
    // 切れるため、ディセンダが収まる行高を確保する。
    lineHeight: '14px',
    boxShadow: `0px 1px 2px 0px ${colorPalette.monotone[4]}${STATUS_CHIP_SHADOW_ALPHA_HEX}`,
    backgroundColor: bg,
    color: fg,
    border: bd,
  })
)

const Label = styled('span')({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
})
